import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { Box, Typography, Chip, Fade } from '@mui/material';
import { Psychology } from '@mui/icons-material';

interface ThinkingSectionProps {
  showThinking: boolean;
  thinkingText: string;
}

const NEAR_BOTTOM_THRESHOLD_PX = 28;
const TYPING_TICK_MS = 35;
const MAX_CHARS_PER_TICK = 5;
const REMAINING_DIVISOR = 20;

export const ThinkingSection: React.FC<ThinkingSectionProps> = ({ 
  showThinking, 
  thinkingText 
}) => {
  const [visibleLength, setVisibleLength] = useState(thinkingText.length);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const shouldStickToBottomRef = useRef(true);

  const isNearBottom = useCallback((element: HTMLDivElement) => {
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;
  }, []);

  useEffect(() => {
    if (thinkingText.length < visibleLength) {
      setVisibleLength(thinkingText.length);
      return;
    }

    if (!showThinking) {
      setVisibleLength(thinkingText.length);
      return;
    }

    if (visibleLength >= thinkingText.length) return;

    let timeoutId: number | undefined;

    const tick = () => {
      setVisibleLength((current) => {
        if (current >= thinkingText.length) return current;
        const remaining = thinkingText.length - current;
        const step = Math.min(Math.max(1, Math.ceil(remaining / REMAINING_DIVISOR)), MAX_CHARS_PER_TICK);
        return Math.min(thinkingText.length, current + step);
      });
      timeoutId = window.setTimeout(tick, TYPING_TICK_MS);
    };

    timeoutId = window.setTimeout(tick, TYPING_TICK_MS);

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [thinkingText, showThinking, visibleLength]);

  const visibleText = useMemo(() => {
    if (!showThinking) return thinkingText;
    return thinkingText.slice(0, visibleLength);
  }, [showThinking, thinkingText, visibleLength]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (shouldStickToBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [visibleText]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    shouldStickToBottomRef.current = isNearBottom(event.currentTarget);
  }, [isNearBottom]);

  if (!showThinking || !thinkingText) return null;

  const isStreaming = visibleLength < thinkingText.length;

  return (
    <Fade in={showThinking} timeout={500}>
      <Box sx={{ 
        mt: 2, 
        p: 2, 
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 2,
        border: '1px solid rgba(148, 163, 184, 0.18)',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.16)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Psychology sx={{ color: '#6366f1', fontSize: '1.25rem' }} />
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: '#e5eef8', 
              fontWeight: 600,
              fontSize: '0.875rem'
            }}
          >
            AI Reasoning Process
          </Typography>
          <Chip
            label="Live"
            size="small"
            sx={{
              backgroundColor: 'rgba(250, 204, 21, 0.14)',
              color: '#fde68a',
              fontWeight: 500,
              fontSize: '0.65rem',
              height: 18,
              border: '1px solid #fde68a'
            }}
          />
        </Box>
        <Box
          ref={scrollContainerRef}
          onScroll={handleScroll}
          sx={{ 
            color: 'rgba(226, 232, 240, 0.84)', 
            fontSize: '0.8rem',
            fontFamily: 'monospace',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            backgroundColor: 'rgba(2, 8, 23, 0.34)',
            p: 1.5,
            borderRadius: 1.5,
            border: '1px solid rgba(148, 163, 184, 0.14)',
            maxHeight: '150px',
            overflowY: 'auto',
            '@keyframes streamingCaretBlink': {
              '0%, 45%': { opacity: 1 },
              '55%, 100%': { opacity: 0.15 },
            },
          }}
        >
          {visibleText}
          {isStreaming && (
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                display: 'inline-block',
                width: '0.7ch',
                height: '1.15em',
                ml: '1px',
                verticalAlign: 'text-bottom',
                borderRadius: '2px',
                background: 'linear-gradient(180deg, rgba(44,192,183,0.95), rgba(103,232,249,0.85))',
                boxShadow: '0 0 12px rgba(44,192,183,0.25)',
                animation: 'streamingCaretBlink 0.95s ease-in-out infinite',
              }}
            />
          )}
        </Box>
      </Box>
    </Fade>
  );
}; 